import { useState, useEffect, useRef } from "react";
import { Route, Switch, useHistory, useLocation } from "react-router-dom";

import Box from "@mui/material/Box";

import { ShoppingCartProvider } from "src/context/ShoppingCartContext";

import Header from "../../features/header";
import Footer from "../../components/Footer";
import Confirm from "../../components/Confirm";
import ConfirmContext from "../../context/ConfirmContext";
import DisplayContext from "../../context/DisplayContext";
import CrewOverview from "../crew-overview";
import PaymentRequired from "../../components/PaymentRequired";
import PermissionDenied from "../../components/PermissionDenied";
import ServerError from "../../components/ServerError";
import useCrew from "../../hooks/useCrew";
import "./index.scss";

const Dashboard = () => {
  const [confirmValues, setConfirmValues] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const { crew } = useCrew();
  const history = useHistory();
  const location = useLocation();
  const previousCrewId = useRef(crew?.id);

  // Listen for crew changes and redirect if needed
  useEffect(() => {
    // If we're on an error page and the user selects a different company,
    // navigate home so the new company context can load normally.
    const isOnErrorPage =
      location.pathname === "/payment-required" ||
      location.pathname === "/permission-denied" ||
      location.pathname === "/server-error";
    const crewIdChanged = previousCrewId.current !== undefined && previousCrewId.current !== crew?.id;

    if (isOnErrorPage && crewIdChanged && crew) {
      history.push("/");
    }

    previousCrewId.current = crew?.id;
  }, [crew?.id, history, location.pathname]);

  const confirmActions = {
    show(title, message, onSuccess) {
      setConfirmValues({
        title,
        message,
        onConfirm(isSuccess) {
          if (isSuccess) {
            onSuccess();
          }
          setConfirmValues(null);
        },
      });
    },
    notification(title, message) {
      setConfirmValues({
        title,
        message,
        notification: true,
        onConfirm() {
          setConfirmValues(null);
        },
      });
    },
  };

  const toggleFullscreen = () => setFullscreen(!fullscreen);

  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      <ConfirmContext.Provider value={confirmActions}>
        <ShoppingCartProvider>
          <DisplayContext.Provider value={{ fullscreen, toggleFullscreen }}>
            <Box
              style={{ height: "100vh", overflow: "hidden" }}
              className={`${fullscreen && "fullscreen"}`}
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                className={`header-wrapper ${fullscreen && "fullscreen"}`}
                sx={{
                  flex: "0 0 auto",
                }}
              >
                <Header menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
              </Box>
              <Box
                className="dashboard-content"
                sx={{
                  flex: "1 1 auto",
                  minHeight: 0,
                  overflow: "hidden",
                }}
              >
                <Switch>
                  <Route path="/payment-required">
                    <PaymentRequired />
                  </Route>
                  <Route path="/permission-denied">
                    <PermissionDenied />
                  </Route>
                  <Route path="/server-error">
                    <ServerError />
                  </Route>
                  <Route>
                    <CrewOverview menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
                  </Route>
                </Switch>
              </Box>
              <Footer />
            </Box>
          </DisplayContext.Provider>
        </ShoppingCartProvider>
      </ConfirmContext.Provider>
      {confirmValues && (
        <Confirm
          title={confirmValues.title}
          onConfirm={confirmValues.onConfirm}
          notification={confirmValues.notification}
        >
          {confirmValues.message}
        </Confirm>
      )}
    </>
  );
};

export default Dashboard;
